// Coalesce waiting jobs by kind; at most two network generations at a time.
// A newer key invalidates a running result without interrupting another kind.
export function createPreparationQueue({ concurrency = 2, onResult, onError }) {
  const pending = new Map();
  const latest = new Map();
  const runningKinds = new Set();
  let active = 0;
  let closed = false;
  const drain = () => {
    while (!closed && active < concurrency && pending.size) {
      const candidate = [...pending].filter(([kind]) => !runningKinds.has(kind)).sort((a, b) => a[1].priority - b[1].priority)[0];
      if (!candidate) return;
      const [kind, job] = candidate;
      pending.delete(kind);
      runningKinds.add(kind);
      active++;
      Promise.resolve().then(job.run).then(result => {
        if (!closed && latest.get(kind) === job) {
          job.result = result;
          onResult(kind, result, job.key);
          job.resolve(result);
        } else job.resolve(null);
      }).catch(error => {
        if (!closed && latest.get(kind) === job) onError?.(kind, error, job.key);
        job.resolve(null);
      }).finally(() => { job.finished = true; active--; runningKinds.delete(kind); drain(); });
    }
  };
  return {
    enqueue(kind, key, run, priority = 1) {
      if (closed) return Promise.resolve(null);
      const existing = latest.get(kind);
      if (existing?.key === key && (!existing.finished || existing.result?.data)) {
        existing.priority = Math.min(existing.priority, priority);
        return existing.promise;
      }
      existing?.resolve(null);
      const job = { key, run, priority };
      job.promise = new Promise(resolve => { job.resolve = resolve; });
      latest.set(kind, job);
      pending.set(kind, job);
      drain();
      return job.promise;
    },
    invalidate(kind) { latest.get(kind)?.resolve(null); latest.delete(kind); pending.delete(kind); },
    close() { closed = true; latest.forEach(job => job.resolve(null)); pending.clear(); },
  };
}
